/**
 * Walking: dragging turns the planet under you, with a glide after you let go; tapping a tile walks
 * there; the arrow keys turn it too. Every frame this moves the planet and finds the tile under you.
 */
import { makeGrid, type Grid } from '@bramvasterd/terrella-rules';

import type { Loop } from '@core/loop';
import { easeInOut, r4 } from '@core/math';
import {
  AXIS_X,
  AXIS_Z,
  quat,
  quatCopy,
  quatFromAxisAngle,
  quatFromUnitVectors,
  quatMultiply,
  quatNormalize,
  quatPremultiply,
  quatSlerp,
  quatApply,
  vec,
  vecNormalize,
} from '@core/quat';
import { stepSpin } from '@game/player/spin';
import type { Game } from '@game/game';
import { tileUnderMe } from '@game/player/local';
import { canWalk } from '@game/view/panels';
import type { World } from '@world/index';

const step = quat();
const w = vec();
const target = quat();
const horizontal = vec();
const point = vec();

export interface Walker {
  /** Turn the planet as a drag of (dx, dy) pixels would. */
  turn(dx: number, dy: number): void;
  /** Turn the view around me by `rad` (clockwise on screen for positive), as two fingers turning would. */
  turnView(rad: number): void;
  walkToTile(tile: number, now: number): void;
  /** Walks to a point on the planet (a unit vector), such as a corner. */
  walkToPoint(x: number, y: number, z: number, now: number): void;
  /** Per frame: keys, momentum, walks, and the tile under me. */
  update(dt: number, now: number, keys: { dx: number; dy: number }): void;
}

export function createWalker(game: Game, world: World, loop: Loop): Walker {
  const local = game.local;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  let grid: Grid | null = null;

  const turn = (dx: number, dy: number): void => {
    // finger pixels to radians: the same hexagons per swipe at every planet size and zoom
    const k = world.fingerScale();
    quatPremultiply(
      local.q,
      quatFromAxisAngle(step, AXIS_Z[0] ?? 0, AXIS_Z[1] ?? 0, AXIS_Z[2] ?? 1, -dx * k),
    );
    quatPremultiply(
      local.q,
      quatFromAxisAngle(step, AXIS_X[0] ?? 1, AXIS_X[1] ?? 0, AXIS_X[2] ?? 0, dy * k),
    );
    quatNormalize(local.q);
    if (Math.abs(dx) + Math.abs(dy) > 0.01) {
      local.heading[0] = -dx;
      local.heading[1] = 0;
      local.heading[2] = -dy;
      vecNormalize(local.heading);
      local.moving = 1;
    }
    loop.wake(300);
  };

  const turnView = (rad: number): void => {
    quatPremultiply(local.q, quatFromAxisAngle(step, 0, 1, 0, -rad));
    quatNormalize(local.q);
    loop.wake(300);
  };

  return {
    turn,
    turnView,
    walkToTile(tile, now) {
      const s = game.store.get();
      if (!s.view) return;
      const g = makeGrid(s.view.subdivisions);
      this.walkToPoint(
        g.centers[tile * 3] ?? 0,
        g.centers[tile * 3 + 1] ?? 1,
        g.centers[tile * 3 + 2] ?? 0,
        now,
      );
    },
    walkToPoint(cx, cy, cz, now) {
      point[0] = cx;
      point[1] = cy;
      point[2] = cz;
      quatApply(w, local.q, point);
      const up = vec(0, 1, 0);
      quatFromUnitVectors(target, w, up);
      quatMultiply(target, target, local.q);
      const dot = Math.max(-1, Math.min(1, w[1] ?? 1));
      const duration = 350 + Math.acos(dot) * 500;
      if (reduced) quatCopy(local.q, target);
      else
        local.walk = {
          from: quatCopy(quat(), local.q),
          to: quatCopy(quat(), target),
          start: now,
          duration,
        };
      horizontal[0] = w[0] ?? 0;
      horizontal[1] = 0;
      horizontal[2] = w[2] ?? 0;
      if (horizontal[0] ** 2 + horizontal[2] ** 2 > 1e-6) {
        local.heading[0] = horizontal[0];
        local.heading[1] = 0;
        local.heading[2] = horizontal[2];
        vecNormalize(local.heading);
      }
      const start = game.clock.serverNow();
      if (start !== null)
        game.events.emit('walk', { to: [r4(cx), r4(cy), r4(cz)], start, duration });
      loop.wake(duration + 100);
    },
    update(dt, now, keys) {
      const s = game.store.get();
      if (!s.view) {
        if (s.here !== -1) game.store.set({ here: -1 });
        return;
      }
      // a panel is open: stay put, but the panel still knows the tile under me
      if (!canWalk(game)) return;
      if (keys.dx || keys.dy) {
        local.walk = null;
        turn(keys.dx * dt * 60, keys.dy * dt * 60);
      } else if (!reduced || !local.dragging) {
        // the heavy planet: chases the finger while dragging, glides after, never faster than the cap
        stepSpin(local.vel, local.finger, local.dragging, dt, now);
        if (local.vel.x !== 0 || local.vel.y !== 0) {
          const frames = dt * 60;
          turn(local.vel.x * frames, local.vel.y * frames);
          loop.wake(100);
        }
      }
      const walk = local.walk;
      if (walk) {
        const p = Math.min(1, (now - walk.start) / walk.duration);
        quatSlerp(local.q, walk.from, walk.to, easeInOut(p));
        local.moving = 1;
        if (p >= 1) local.walk = null;
        loop.wake(100);
      }
      local.moving = Math.max(0, local.moving - dt * 3);
      if (grid?.subdivisions !== s.view.subdivisions) grid = makeGrid(s.view.subdivisions);
      const here = tileUnderMe(local, grid, s.here);
      if (here !== s.here) game.store.set({ here });
    },
  };
}
